/**************************************************
 * hooks/dash/useDragScroll.js
 *
 * @author  LSJ
 * @since   1.0 (2025-02-28)
 * @version 1.0
 **************************************************/
import { useRef, useState } from "react";

/**
 * useDragScroll
 *
 * 컨테이너 내에서 마우스 드래그로 수평 스크롤을 가능하게 합니다.
 * 드래그 시작 위치, 현재 스크롤 위치를 기록하고, 마우스 이동에 따라 스크롤을 조정합니다.
 *
 * @returns {object}  containerRef  스크롤 컨테이너의 ref
 *                    eventHandlers 마우스 이벤트 핸들러들
 *                    cursorStyle   드래그 상태에 따른 커서 스타일
 */
const useDragScroll = () => {
  // 스크롤 컨테이너에 대한 ref
  const containerRef = useRef(null);

  // 마우스 다운 상태 여부
  const [isDown, setIsDown] = useState(false);
  // 드래그 시작 시의 X 좌표 (컨테이너 기준)
  const [startX, setStartX] = useState(0);
  // 드래그 시작 시의 컨테이너 스크롤 위치
  const [scrollLeft, setScrollLeft] = useState(0);

  /**
   * 마우스 다운 이벤트 핸들러
   * 드래그를 시작하며 시작 좌표와 현재 스크롤 위치를 저장합니다.
   */
  const handleMouseDown = (e) => {
    setIsDown(true);
    // 컨테이너의 왼쪽 경계로부터의 X 좌표
    setStartX(e.pageX - containerRef.current.offsetLeft);
    // 현재 스크롤 위치 저장
    setScrollLeft(containerRef.current.scrollLeft);
  };

  /**
   * 마우스가 컨테이너 영역을 벗어나거나 마우스 업 시, 드래그 상태 종료
   */
  const handleMouseLeave = () => setIsDown(false);
  const handleMouseUp = () => setIsDown(false);

  /**
   * 마우스 이동 이벤트 핸들러
   * 드래그 중일 때, 마우스 이동 거리를 계산하여 스크롤 위치를 조정합니다.
   */
  const handleMouseMove = (e) => {
    if (!isDown) return;
    e.preventDefault();
    // 현재 마우스 X 좌표 (컨테이너 기준)
    const x = e.pageX - containerRef.current.offsetLeft;
    // 드래그한 거리 (배율로 속도 조절 가능)
    const walk = (x - startX) * 1;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  // 마우스 이벤트 핸들러 객체
  const eventHandlers = {
    onMouseDown: handleMouseDown,
    onMouseLeave: handleMouseLeave,
    onMouseUp: handleMouseUp,
    onMouseMove: handleMouseMove,
  };

  // 드래그 중이면 'grabbing', 아니면 'grab' 커서 적용
  const cursorStyle = isDown ? "grabbing" : "grab";

  return { containerRef, eventHandlers, cursorStyle };
};

export default useDragScroll;
